import React, { useEffect, useMemo } from 'react';
import {
  Plan,
  PLAN_CREATED,
  PLAN_DELETED,
  PLAN_PROJECT_TYPE_UPDATED,
  PLAN_UPDATED,
  ProjectType,
  useEventsSubscriber
} from 'nekst-api';
import {
  Dropdown,
  FormTemplate,
  IfFormData,
  Option,
  RelativeRow,
  TextLink,
  useFormContextRequired
} from 'ui-builder';
import {
  SetupAreaConstrainedProjectTypeDropdown,
  TeamSetupAreaIdDropdown
} from 'features/tasks-forms';
import useFeatureToggleService from 'feature-toggle';
import { useDataLoaderNew } from 'data-loader';

export interface ChoosePlanDropdownWidgetProps {
  value?: number;
  getPlansFunc: () => Promise<Plan[]>;
  openListPage: () => void,
  openPlanPage: (planId: number) => void,
  showMarketsDropdown?: boolean;
}

const ALL_PROJECT_TYPES_VALUE = null;

function MarketsDropdown() {
  return (
    <TeamSetupAreaIdDropdown
      source="setupAreaId"
      withAllOption
    />
  );
}

function PlanProjectTypeDropdown() {
  return (
    <SetupAreaConstrainedProjectTypeDropdown
      additionalOptions={[{
        value: null,
        label: 'All'
      }]}
      resetValue={ALL_PROJECT_TYPES_VALUE}
    />
  );
}

function PlanIdDropdown(props: {
  openPlanPage: (planId: number) => void,
  openListPage: () => void,
  getPlansFunc: () => Promise<Plan[]>,
}) {

  const formContext = useFormContextRequired<{
    projectType?: ProjectType | 'ALL',
    setupAreaId?: number,
  }>();

  const {
    projectType,
    setupAreaId
  } = formContext.data!;


  const {
    data: allOptions,
    version: allOptionsVersion
  } = useDataLoaderNew(
    async () => {
      const plans = await props.getPlansFunc();

      return plans.map((item) => {
        let label = item.name;

        if (item._detailsFieldsSet) {
          label += ` (${item._detailsFieldsSet.name})`;
        }

        return {
          value: item.id,
          label: label,
          projectType: item.projectType,
          setupAreaId: item._setupArea?.id,
          groupName: item._setupArea?.name || 'No Market'
        };
      });
    },
    {
      events: [PLAN_UPDATED, PLAN_CREATED, PLAN_DELETED, PLAN_PROJECT_TYPE_UPDATED],
      dependencies: [formContext.data?.projectType]
    }
  );

  useEventsSubscriber(
    'ChosePlanDropdownWidget',
    {
      [PLAN_PROJECT_TYPE_UPDATED]: (eventData) => {
        formContext.onChangeCallback({
          projectType: eventData.projectType
        });
      }
    }
  );

  const visiblePlanOptions = useMemo(() => {
    if (allOptions) {
      let result = allOptions;

      if (projectType !== null) {
        result = result.filter((item) => {
          return item.projectType === projectType;
        });
      }

      if (setupAreaId) {
        result = result.filter((item) => item.setupAreaId === setupAreaId);
      }

      return result;
    } else {
      return [];
    }

  }, [projectType, allOptionsVersion, setupAreaId]);

  let dropdownOptions;
  if (setupAreaId) {
    dropdownOptions = visiblePlanOptions;
  } else {
    dropdownOptions = Object.values(visiblePlanOptions.reduce((acc, item) => {
      const groupName = item.groupName;

      if (!acc[groupName]) {
        acc[groupName] = {
          label: groupName,
          options: []
        };
      }
      acc[groupName].options.push(
        item
      );

      return acc;
    }, {} as Record<string, { label: string, options: Option[] }>))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  return (
    <Dropdown
      options={dropdownOptions}
      source="planId"
      placeholder={!projectType ? 'Choose transaction type' : 'Choose workflow'}
      label="Select an existing workflow"
    />
  );
}

function GoToListLink(props: { openListPage: () => void }) {
  const formContext = useFormContextRequired();

  return (
    <TextLink
      href="/plans"
      onClick={() => {
        formContext.onChangeCallback(({
          planId: undefined,
        }));
      }}
    >
      ← Go back to the workflows list
    </TextLink>
  );
}

function PlanRoutesNavigator(
  props: {
    openPlanPage: (planId: number) => void,
    openListPage: () => void,
  }
) {
  const formContext = useFormContextRequired();

  const planId = formContext.data?.planId;

  useEffect(() => {
    if (planId) {
      props.openPlanPage(planId);
    } else {
      props.openListPage();
    }
  }, [planId]);

  return null;
}

export default function ChoosePlanDropdownWidget(props: ChoosePlanDropdownWidgetProps) {
  const featureToggleService = useFeatureToggleService();

  const isMarketsEnabled = featureToggleService.isNewTransactionsManagementEnabled();

  return (
    <FormTemplate
      initialData={{
        projectType: null,
        setupAreaId: null,
        planId: props.value
      }}
      submitFormFunc={async () => {
        // no actual submit
      }}
      buttons={null}
      dependencies={[props.value]}
    >
      <IfFormData conditionFunc={(data) => !data.planId}>
        <RelativeRow weights={isMarketsEnabled ? [4, 3, 5] : [4, 8]}>
          {props.showMarketsDropdown ? (
            <MarketsDropdown />
          ) : null}
          <PlanProjectTypeDropdown />
          <PlanIdDropdown
            openPlanPage={props.openPlanPage}
            openListPage={props.openListPage}
            getPlansFunc={props.getPlansFunc}
          />
        </RelativeRow>
      </IfFormData>
      <IfFormData conditionFunc={(data) => data.planId}>
        <GoToListLink openListPage={props.openListPage} />
      </IfFormData>
      <PlanRoutesNavigator openListPage={props.openListPage} openPlanPage={props.openPlanPage} />
    </FormTemplate>
  );

}
